// src/pages/Production/Subcontractors.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, writeBatch, doc, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: { 
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: { 
        padding: '12px 24px', 
        border: 'none', 
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)', 
        color: 'white', 
        borderRadius: '12px', 
        cursor: 'pointer', 
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: { 
        background: 'white', 
        padding: '32px', 
        borderRadius: '16px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    searchContainer: {
        marginBottom: '24px'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: { 
        padding: '12px 16px 12px 44px', 
        fontSize: '14px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '12px', 
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: { 
        width: '100%', 
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: { 
        background: '#f8fafc', 
        padding: '16px 20px', 
        textAlign: 'left', 
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: { 
        padding: '16px 20px', 
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: { 
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    modalOverlay: { 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        zIndex: 100 
    },
    modalContent: { 
        background: 'white', 
        padding: '32px', 
        borderRadius: '16px', 
        width: '90%', 
        maxWidth: '800px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: { 
        display: 'block', 
        marginBottom: '8px', 
        fontWeight: '600', 
        color: '#374151',
        fontSize: '14px'
    },
    input: { 
        width: '100%', 
        padding: '12px 16px', 
        fontSize: '14px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px', 
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
        minHeight: '100px',
        resize: 'vertical'
    },
    badge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        marginLeft: '8px'
    },
    activeBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    inactiveBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    }
};

function AddSubcontractorModal({ onClose, onSave }) {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        taxOffice: '',
        taxNumber: '',
        productionTypes: [],
        costTariff: []
    });

    const [newProductionType, setNewProductionType] = useState('');
    const [newCostItem, setNewCostItem] = useState({
        type: 'per_unit',
        value: 0,
        unit: 'Adet',
        currency: 'TRY'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleProductionTypeAdd = () => {
        if (newProductionType.trim() && !formData.productionTypes.includes(newProductionType.trim())) {
            setFormData(prev => ({
                ...prev,
                productionTypes: [...prev.productionTypes, newProductionType.trim()]
            }));
            setNewProductionType('');
        }
    };

    const handleProductionTypeRemove = (index) => {
        setFormData(prev => ({
            ...prev,
            productionTypes: prev.productionTypes.filter((_, i) => i !== index)
        }));
    };

    const handleCostItemAdd = () => {
        setFormData(prev => ({
            ...prev,
            costTariff: [...prev.costTariff, { ...newCostItem, id: Date.now().toString() }]
        }));
        setNewCostItem({
            type: 'per_unit',
            value: 0,
            unit: 'Adet',
            currency: 'TRY'
        });
    };

    const handleCostItemRemove = (index) => {
        setFormData(prev => ({
            ...prev,
            costTariff: prev.costTariff.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Yeni Fason Tedarikçi Ekle</h2>
                    <button 
                        onClick={onClose}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            fontSize: '24px', 
                            cursor: 'pointer',
                            color: '#94a3b8'
                        }}
                    >
                        ×
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Firma Adı *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            style={styles.input}
                            placeholder="Firma adı girin"
                        />
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>E-posta</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="E-posta adresi girin"
                            />
                        </div>
                        
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Telefon</label>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Telefon numarası girin"
                            />
                        </div>
                    </div>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Adres</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            style={styles.input}
                            placeholder="Adres girin"
                        />
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Vergi Dairesi</label>
                            <input
                                type="text"
                                name="taxOffice"
                                value={formData.taxOffice}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Vergi dairesi girin"
                            />
                        </div>
                        
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Vergi Numarası</label>
                            <input
                                type="text"
                                name="taxNumber"
                                value={formData.taxNumber}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Vergi numarası girin"
                            />
                        </div>
                    </div>
                    
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px' }}>
                        Üretim Türleri
                    </h3>
                    
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                        <input
                            type="text"
                            value={newProductionType}
                            onChange={(e) => setNewProductionType(e.target.value)}
                            style={styles.input}
                            placeholder="Üretim türü girin"
                        />
                        <button
                            type="button"
                            onClick={handleProductionTypeAdd}
                            style={{
                                ...styles.button,
                                padding: '12px 20px',
                                background: '#f1f5f9',
                                color: '#374151'
                            }}
                        >
                            Ekle
                        </button>
                    </div>
                    
                    {formData.productionTypes.length > 0 && (
                        <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '8px', 
                            marginBottom: '20px' 
                        }}>
                            {formData.productionTypes.map((type, index) => (
                                <div key={index} style={{ 
                                    background: '#e2e8f0', 
                                    padding: '6px 12px', 
                                    borderRadius: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}>
                                    {type}
                                    <button
                                        type="button"
                                        onClick={() => handleProductionTypeRemove(index)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#ef4444',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            padding: 0
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginTop: '30px', marginBottom: '15px' }}>
                        Maliyet Tarifesi
                    </h3>
                    
                    <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr 1fr 1fr auto', 
                        gap: '10px', 
                        marginBottom: '15px',
                        alignItems: 'end'
                    }}>
                        <div>
                            <label style={{...styles.label, fontSize: '12px'}}>Tür</label>
                            <select
                                value={newCostItem.type}
                                onChange={(e) => setNewCostItem(prev => ({ ...prev, type: e.target.value }))}
                                style={styles.select}
                            >
                                <option value="per_unit">Adet Başına</option>
                                <option value="per_kg">Kilo Başına</option>
                                <option value="per_job">İş Başına</option>
                            </select>
                        </div>
                        
                        <div>
                            <label style={{...styles.label, fontSize: '12px'}}>Değer</label>
                            <input
                                type="number"
                                value={newCostItem.value}
                                onChange={(e) => setNewCostItem(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                                style={styles.input}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        
                        <div>
                            <label style={{...styles.label, fontSize: '12px'}}>Birim</label>
                            <select
                                value={newCostItem.unit}
                                onChange={(e) => setNewCostItem(prev => ({ ...prev, unit: e.target.value }))}
                                style={styles.select}
                            >
                                <option value="Adet">Adet</option>
                                <option value="Kg">Kg</option>
                                <option value="Metre">Metre</option>
                            </select>
                        </div>
                        
                        <div>
                            <label style={{...styles.label, fontSize: '12px'}}>Para Birimi</label>
                            <select
                                value={newCostItem.currency}
                                onChange={(e) => setNewCostItem(prev => ({ ...prev, currency: e.target.value }))}
                                style={styles.select}
                            >
                                <option value="TRY">₺ (TRY)</option>
                                <option value="USD">$ (USD)</option>
                                <option value="EUR">€ (EUR)</option>
                            </select>
                        </div>
                        
                        <button
                            type="button"
                            onClick={handleCostItemAdd}
                            style={{
                                ...styles.button,
                                padding: '12px 16px',
                                background: '#f1f5f9',
                                color: '#374151'
                            }}
                        >
                            Ekle
                        </button>
                    </div>
                    
                    {formData.costTariff.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <table style={{...styles.table, margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{...styles.th, background: '#f1f5f9'}}>Tür</th>
                                        <th style={{...styles.th, background: '#f1f5f9'}}>Değer</th>
                                        <th style={{...styles.th, background: '#f1f5f9'}}>Birim</th>
                                        <th style={{...styles.th, background: '#f1f5f9'}}>Para Birimi</th>
                                        <th style={{...styles.th, background: '#f1f5f9'}}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData.costTariff.map((item, index) => (
                                        <tr key={item.id}>
                                            <td style={styles.td}>
                                                {item.type === 'per_unit' && 'Adet Başına'}
                                                {item.type === 'per_kg' && 'Kilo Başına'}
                                                {item.type === 'per_job' && 'İş Başına'}
                                            </td>
                                            <td style={styles.td}>{item.value}</td>
                                            <td style={styles.td}>{item.unit}</td>
                                            <td style={styles.td}>{item.currency}</td>
                                            <td style={styles.td}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCostItemRemove(index)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#ef4444',
                                                        cursor: 'pointer',
                                                        fontSize: '18px'
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    <div style={styles.modalActions}>
                        <button 
                            type="button"
                            onClick={onClose}
                            style={{ 
                                ...styles.button, 
                                background: '#f1f5f9', 
                                color: '#374151',
                                padding: '10px 20px'
                            }}
                        >
                            İptal
                        </button>
                        <button 
                            type="submit"
                            style={{ 
                                ...styles.button, 
                                padding: '10px 20px'
                            }}
                        >
                            Fason Tedarikçiyi Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Subcontractors() {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [subcontractors, setSubcontractors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    // Fetch real data from the database
    useEffect(() => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;
        
        // Fetch subcontractors
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'subcontractors')), 
            snap => setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        return () => { 
            unsubSubcontractors(); 
        };
    }, [userData]);

    const handleAddSubcontractor = async (subcontractorData) => {
        try {
            // Save the subcontractor to the database
            await addDoc(collection(db, 'companies', userData.companyId, 'subcontractors'), {
                ...subcontractorData,
                companyId: userData.companyId,
                balance: 0,
                isActive: true,
                createdAt: serverTimestamp()
            });
            setShowAddModal(false);
            alert('Fason tedarikçi başarıyla kaydedildi!');
        } catch (error) {
            console.error("Error adding subcontractor:", error);
            alert('Fason tedarikçi eklenirken bir hata oluştu: ' + error.message);
        }
    };

    const filteredSubcontractors = subcontractors.filter(sub =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.productionTypes && sub.productionTypes.some(type => type.toLowerCase().includes(searchTerm.toLowerCase())))
    );

    const getStatusBadge = (isActive) => {
        return isActive ? (
            <span style={{...styles.badge, ...styles.activeBadge}}>Aktif</span>
        ) : (
            <span style={{...styles.badge, ...styles.inactiveBadge}}>Pasif</span>
        );
    };

    const formatCurrency = (amount, currency) => {
        const formatter = new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency || 'TRY'
        });
        return formatter.format(amount);
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Fason Tedarikçiler</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Fason üretim yapan tedarikçileri yönetin
                    </p>
                </div>
                <div style={styles.headerActions}>
                    <button 
                        onClick={() => setShowAddModal(true)}
                        style={styles.button}
                    >
                        + Yeni Fason Tedarikçi
                    </button>
                </div>
            </div>

            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Fason tedarikçi veya üretim türü ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                {filteredSubcontractors.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📋</div>
                        <h3 style={styles.emptyText}>Fason Tedarikçi Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            {searchTerm ? 'Arama kriterlerinize uygun fason tedarikçi bulunamadı.' : 'Henüz hiç fason tedarikçi eklenmemiş.'}
                        </p>
                        <button 
                            onClick={() => setShowAddModal(true)}
                            style={{...styles.button, marginTop: '20px'}}
                        >
                            İlk Fason Tedarikçinizi Ekleyin
                        </button>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Firma Adı</th>
                                    <th style={styles.th}>Üretim Türleri</th>
                                    <th style={styles.th}>Bakiye</th>
                                    <th style={styles.th}>Durum</th>
                                    <th style={styles.th}>Kayıt Tarihi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubcontractors.map(sub => (
                                    <tr 
                                        key={sub.id} 
                                        style={{...styles.trHover, ...(hoveredRow === sub.id ? styles.trHoverActive : {})}} 
                                        onMouseEnter={() => setHoveredRow(sub.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/uretim/fason-tedarikciler/${sub.id}`)}
                                    >
                                        <td style={styles.td}>
                                            <div style={{ fontWeight: '600' }}>{sub.name}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                                {sub.email || sub.phone}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                {sub.productionTypes && sub.productionTypes.slice(0, 2).map((type, index) => (
                                                    <span key={index} style={{ 
                                                        background: '#e2e8f0', 
                                                        padding: '2px 8px', 
                                                        borderRadius: '12px',
                                                        fontSize: '11px'
                                                    }}>
                                                        {type}
                                                    </span>
                                                ))}
                                                {sub.productionTypes && sub.productionTypes.length > 2 && (
                                                    <span style={{ 
                                                        background: '#e2e8f0', 
                                                        padding: '2px 8px', 
                                                        borderRadius: '12px',
                                                        fontSize: '11px'
                                                    }}>
                                                        +{sub.productionTypes.length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{...styles.td, color: sub.balance < 0 ? '#dc2626' : '#10b981', fontWeight: '600'}}>
                                            {formatCurrency(sub.balance || 0, 'TRY')}
                                        </td>
                                        <td style={styles.td}>
                                            {getStatusBadge(sub.isActive !== false)}
                                        </td>
                                        <td style={styles.td}>
                                            {sub.createdAt?.toDate ? sub.createdAt.toDate().toLocaleDateString('tr-TR') : 
                                             sub.createdAt instanceof Date ? sub.createdAt.toLocaleDateString('tr-TR') :
                                             'Tarih yok'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {showAddModal && (
                <AddSubcontractorModal 
                    onClose={() => setShowAddModal(false)} 
                    onSave={handleAddSubcontractor} 
                />
            )}
        </div>
    );
}

export default Subcontractors;